import React, { useState } from 'react';
import { DevicesStoreInfoResponse } from '../../../../../service/store/StoreService.model';
import { useNavigate } from 'react-router-dom';
import PaperButton from '../../../../../theme/ui/Atoms/PaperButton/PaperButton';
import { CircularProgress, Typography } from '@mui/material';
import InputSelect from '../../../../../theme/ui/Atoms/InputSelect';
import DeviceType from '../../../InstallFacilities/DevicesManagerOverviewSetUp/DeviceType';
import StatusOverviewByDevice from './StatusOverviewByDevice';
import { RSSIOverviewByDevice } from './RSSIOverviewByDevice';
import { DeviceActions } from './DeviceActions';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import FlexeserveDeviceAPI from '../../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import useErrorMessage from '../../../../../shared/hooks/useErrorMessage';
import useMessage from '../../../../../shared/hooks/useMessage';
import GroupAPI, {
  useGetGroupByIdQuery,
} from '../../../../../service/group/GroupAPI';
import useViewLocationsPath from '../../../../../shared/hooks/useViewLocationsPath';
import Badge from '../../../../../theme/ui/Atoms/Badge/Badge';
import { AlarmsDevice } from './AlarmsDevice';
import Input from '../../../../../theme/ui/Atoms/Input';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDeviceNameOptions } from '../../../../../redux/workspace/util';

interface DeviceUnitProps {
  device: DevicesStoreInfoResponse;
  deviceEditing: string | null;
  setDeviceEditing: (editing: string | null) => void;
  duplicated?:
    | {
        id: string;
        type: string[];
        label: string[];
      }
    | undefined;
  // it means that is safe delete the commander also
  isLastComposableDevice?: boolean;
  schedules: {
    optionsSchedule: { text: string; value: string }[];
    isLoadingSchedules?: boolean;
    isFetchingSchedules?: boolean;
    displaySchedule?: boolean;
  };
  showRSSIColumn?: boolean;
}

export const DeviceUnit = (props: DeviceUnitProps) => {
  const {
    device,
    deviceEditing,
    setDeviceEditing,
    duplicated,
    schedules,
    showRSSIColumn,
  } = props;
  const unitNameOptions = useDeviceNameOptions();
  const group = useGetGroupByIdQuery(device.composableDeviceId ?? '', {
    skip: !device.composableDeviceId,
  }).data;
  const {
    optionsSchedule,
    isLoadingSchedules,
    isFetchingSchedules,
    displaySchedule,
  } = schedules;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getStoreDevicePath } = useViewLocationsPath();
  const [displayRemoveDeviceModal, setDisplayRemoveDeviceModal] =
    useState(false);

  const [updateFlexeserveDevice, { isLoading: isUpdatingDevice }] =
    FlexeserveDeviceAPI.useUpdateSetupDeviceMutation();
  const [updateComposableDevice, { isLoading: isUpdatingGroup }] =
    GroupAPI.useUpdateGroupMutation();

  const onClickDevice = (device: DevicesStoreInfoResponse) => {
    if (deviceEditing !== null || displayRemoveDeviceModal) return;
    navigate(getStoreDevicePath(device.storeId, device.uuid, 'actions'));
  };

  const validationSchema = yup.object({
    unitName: yup.string().required('initial_setup.device_name_required'),
    machineTypeId: yup.string().required('initial_setup.device_name_required'),
  });

  const showErrorMessage = useErrorMessage();
  const showMessage = useMessage();

  const formik = useFormik({
    initialValues: {
      unitName: device.unitName,
      machineTypeId: group?.composableDevice.machineTypeId ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const composableDevice = {
        id: device.composableDeviceId ?? '',
        data: {
          name: device.unitName,
          composableDevice: {
            ...group?.composableDevice,
            channels: group?.composableDevice.channels ?? [],
            labels: group?.composableDevice.labels ?? {},
            controlUnits: group?.composableDevice.controlUnits ?? [],
            machineTypeId: values.machineTypeId,
          },
        },
      };

      return Promise.all([
        updateFlexeserveDevice({ ...device, unitName: values.unitName }),
        updateComposableDevice(composableDevice),
      ])
        .then(() => showMessage(t('device.saved_successful_message')))
        .catch(showErrorMessage)
        .finally(() => setDeviceEditing(null));
    },
  });

  const isEditing = deviceEditing === device.uuid;
  const isDeviceSelected = deviceEditing !== null;
  const selectedScheduleName =
    optionsSchedule.find((schedule) => schedule.value === device.schedule)
      ?.text ?? '';

  return (
    <PaperButton
      component={'tr'}
      key={device.uuid}
      elevation={undefined}
      onClick={() => onClickDevice(device)}
      sx={{
        opacity: isEditing || !isDeviceSelected ? 1 : 0.4,
        cursor: isEditing || !isDeviceSelected ? 'pointer' : 'default',
      }}
    >
      <td
        style={{
          position: 'relative',
        }}
      >
        {duplicated
          ? duplicated?.type.map((errorType, index) => (
              <Badge
                key={index}
                type={errorType}
                sx={{ top: `${index * 2}em` }}
              >
                <Typography>{t(duplicated.label[index])}</Typography>
              </Badge>
            ))
          : null}
        <Typography
          variant="body1"
          component="div"
          textAlign={'left'}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
            display: 'block',
            position: 'relative',
          }}
        >
          {!isEditing ? (
            device.unitName
          ) : (
            <InputSelect
              select
              id={`${device.uuid}-name`}
              name={`${device.uuid}-name`}
              options={unitNameOptions.map((name) => ({
                value: name,
                text: name,
              }))}
              placeholder={t('initial_setup.scan_air_connect.device_name')}
              fullWidth
              required
              aria-required
              value={formik.values.unitName}
              onChange={(v: string) => formik.setFieldValue('unitName', v)}
              onBlur={formik.handleBlur}
            />
          )}
        </Typography>
      </td>
      <td>
        <Typography variant="body1" textAlign={'center'}>
          <DeviceType
            device={device}
            isEditing={isEditing}
            onSelectType={(type) => formik.setFieldValue('machineTypeId', type)}
            formik={formik}
          />
        </Typography>
      </td>
      {!!displaySchedule && (
        <td>
          <Typography
            variant="body1"
            component="div"
            textAlign={'center'}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              navigate(
                getStoreDevicePath(device.storeId, device.uuid, 'schedule')
              );
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
            }}
          >
            {isLoadingSchedules || isFetchingSchedules ? (
              <CircularProgress size={'1em'} />
            ) : (
              <Input
                id={`${device.uuid}-schedule`}
                name={`${device.uuid}-schedule`}
                options={optionsSchedule}
                value={selectedScheduleName}
                disabled={isDeviceSelected && !isEditing}
                fullWidth
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  if (isDeviceSelected && !isEditing) return;
                  navigate(
                    getStoreDevicePath(device.storeId, device.uuid, 'schedule')
                  );
                }}
                onMouseDown={(e: any) => e.stopPropagation()}
                endAdornment={
                  <span>
                    <ExpandMoreIcon color="inherit" />
                  </span>
                }
              />
            )}
          </Typography>
        </td>
      )}
      <td>
        <StatusOverviewByDevice device={device} />
      </td>
      <td>
        <AlarmsDevice device={device} />
      </td>
      {showRSSIColumn && (
        <td>
          <RSSIOverviewByDevice device={device} />
        </td>
      )}
      <td>
        <DeviceActions
          device={device}
          editingDeviceId={deviceEditing}
          isEditing={isDeviceSelected}
          setIsEditing={(editing) =>
            setDeviceEditing(editing ? device.uuid : null)
          }
          onSave={() => formik.handleSubmit()}
          isSaving={isUpdatingDevice || isUpdatingGroup}
          displayRemoveDeviceModal={displayRemoveDeviceModal}
          setDisplayRemoveDeviceModal={setDisplayRemoveDeviceModal}
          canRemoveNode={!duplicated?.type.includes('duplicated')}
          isLastComposableDevice={props.isLastComposableDevice}
        />
      </td>
    </PaperButton>
  );
};
